import React from 'react';
import ReactDOM from 'react-dom/client';
import { App, type AppProps, type ThemeMode } from './App';

export type { AppProps, ThemeMode };
export type SinglePilotContext = NonNullable<AppProps['context']>;

export interface SinglePilotConfig {
  /** WebSocket server URL */
  serverUrl?: string;
  /** Target DOM element or CSS selector */
  target?: string | HTMLElement;
  /** Display mode */
  mode?: 'fullpage' | 'drawer' | 'inline';
  /** Drawer trigger mode: 'fab' shows floating button, 'manual' hides it */
  triggerMode?: 'fab' | 'manual';
  /** Theme mode: 'auto' follows the host/system preference */
  theme?: ThemeMode;
  /** Enable developer inspector panel (shows raw WebSocket traffic) */
  devMode?: boolean;
  /** Available ACD clusters */
  clusters?: Array<{ id: string; label: string }>;
  /** Auto-set context (skip ConnectForm) */
  context?: SinglePilotContext;
  /** Called when the UI is mounted and ready */
  onReady?: () => void;
  /** Called when the drawer opens (drawer mode only) */
  onDrawerOpen?: () => void;
  /** Called when the drawer closes (drawer mode only) */
  onDrawerClose?: () => void;
  /** Called when the auth token expires — should return a fresh token string */
  onTokenRefresh?: () => Promise<string>;
}

let root: ReactDOM.Root | null = null;
let drawerApi: { open: () => void; close: () => void; toggle: () => void } | null = null;
let currentConfig: SinglePilotConfig = {};

function renderApp(): void {
  if (!root) return;
  root.render(
    React.createElement(App, {
      serverUrl: currentConfig.serverUrl,
      mode: currentConfig.mode ?? 'fullpage',
      triggerMode: currentConfig.triggerMode ?? 'fab',
      theme: currentConfig.theme ?? 'auto',
      devMode: currentConfig.devMode ?? false,
      clusters: currentConfig.clusters,
      context: currentConfig.context,
      onReady: currentConfig.onReady,
      onDrawerOpen: currentConfig.onDrawerOpen,
      onDrawerClose: currentConfig.onDrawerClose,
      onDrawerApiReady: (api) => { drawerApi = api; },
      onTokenRefresh: currentConfig.onTokenRefresh,
    }),
  );
}

export function init(config: SinglePilotConfig = {}): void {
  let targetEl: Element | null;

  if (config.target) {
    targetEl =
      typeof config.target === 'string'
        ? document.querySelector(config.target)
        : config.target;
  } else {
    // Auto-create mount point
    targetEl = document.getElementById('singlepilot-root');
    if (!targetEl) {
      targetEl = document.createElement('div');
      targetEl.id = 'singlepilot-root';
      document.body.appendChild(targetEl);
    }
  }

  if (!targetEl) {
    console.error('[SinglePilot] Target element not found:', config.target);
    return;
  }

  currentConfig = {
    ...config,
    theme: config.theme ?? currentConfig.theme ?? 'auto',
  };
  root = ReactDOM.createRoot(targetEl);
  renderApp();
}

/** Switch the theme at runtime (host platform calls this on dark/light toggle) */
export function setTheme(theme: ThemeMode): void {
  currentConfig = { ...currentConfig, theme };
  renderApp();
}

/** Merge updated host-page context and resend it when connected */
export function updateContext(context: Partial<SinglePilotContext>): void {
  currentConfig = {
    ...currentConfig,
    context: {
      ...(currentConfig.context ?? {}),
      ...context,
    },
  };
  renderApp();
}

export function destroy(): void {
  root?.unmount();
  root = null;
  drawerApi = null;
  currentConfig = {};
}

/** Open the drawer (drawer mode only) */
export function open(): void {
  drawerApi?.open();
}

/** Close the drawer (drawer mode only) */
export function close(): void {
  drawerApi?.close();
}

/** Toggle the drawer (drawer mode only) */
export function toggle(): void {
  drawerApi?.toggle();
}

// Expose on window for external use (e.g., ACD script tag integration)
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).SinglePilot = {
    init,
    destroy,
    open,
    close,
    toggle,
    setTheme,
    updateContext,
  };
}

// Auto-init from script tag data attributes
if (typeof document !== 'undefined') {
  const script = document.currentScript as HTMLScriptElement | null;
  if (script?.dataset['api']) {
    document.addEventListener('DOMContentLoaded', () => {
      init({
        serverUrl: script.dataset['api'],
        mode: (script.dataset['mode'] as AppProps['mode']) ?? 'fullpage',
        theme: (script.dataset['theme'] as ThemeMode | undefined) ?? 'auto',
      });
    });
  }
}
